import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
} from '@mui/icons-material';
import PageHeader from '../components/PageHeader';
import LabelIcon from '@mui/icons-material/Label';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import RadioIcon from '@mui/icons-material/Radio';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export default function Categories() {
  const { hasSystemFeature } = useAuth();
  const [categories, setCategories] = useState([]);
  const [stations, setStations] = useState([]);
  const [selectedStationFilter, setSelectedStationFilter] = useState('');
  const [categoryTypeFilter, setCategoryTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [organizeMode, setOrganizeMode] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    color: '#1976D2',
    description: '',
    station_id: null,
    station_ids: [],
    ai_insights_enabled: false,
    metadata_enabled: true,
    category_type: 'other',
  });

  useEffect(() => {
    fetchCategories();
    fetchStations();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`, {
        params: { top_level_only: 'true' },
        withCredentials: true
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to load categories');
    }
  };

  const fetchStations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/stations`, {
        withCredentials: true
      });
      setStations(response.data);
    } catch (error) {
      console.error('Error fetching stations:', error);
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || '',
        color: category.color || '#1976D2',
        description: category.description || '',
        station_id: category.station_id || null,
        station_ids: Array.isArray(category.station_ids)
          ? category.station_ids.map((id) => String(id))
          : category.station_id
            ? [String(category.station_id)]
            : [],
        ai_insights_enabled: category.ai_insights_enabled || false,
        metadata_enabled: category.metadata_enabled !== false,
        category_type: category.category_type || 'other',
      });
    } else {
      setEditingCategory(null);
      const defaultStationId = stations.length === 1 ? String(stations[0].id) : null;
      setFormData({
        name: '',
        color: '#1976D2',
        description: '',
        station_id: defaultStationId ? Number(defaultStationId) : null,
        station_ids: defaultStationId ? [defaultStationId] : [],
        ai_insights_enabled: false,
        metadata_enabled: true,
        category_type: 'other',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setError('');
  };

  const handleSave = async () => {
    try {
      setError('');
      
      if (!formData.name) {
        setError('Category name is required');
        return;
      }

      const selectedStationIds = Array.isArray(formData.station_ids)
        ? formData.station_ids.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
        : [];
      const stationIds = selectedStationIds.length > 0
        ? selectedStationIds
        : stations.length === 1
          ? [Number(stations[0].id)]
          : [];

      const payload = {
        ...formData,
        station_ids: stationIds,
        station_id: stationIds.length === 1 ? stationIds[0] : null,
        ai_insights_enabled: hasSystemFeature('ai_insights')
          ? !!formData.ai_insights_enabled
          : !!editingCategory && !!formData.ai_insights_enabled,
      };

      if (editingCategory) {
        await axios.put(
          `${API_URL}/api/categories/${editingCategory.id}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_URL}/api/categories`,
          payload,
          { withCredentials: true }
        );
      }

      handleCloseDialog();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      setError(error.response?.data?.message || 'Failed to save category');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/categories/${categoryId}`, {
        withCredentials: true
      });
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert(error.response?.data?.message || 'Failed to delete category');
    }
  };

  const filteredCategories = selectedStationFilter
    ? categories.filter((category) => {
        const selected = String(selectedStationFilter);
        const stationIds = Array.isArray(category.station_ids)
          ? category.station_ids.map((id) => String(id))
          : [];
        const legacyStationId = category.station_id == null ? '' : String(category.station_id);
        const isMappedCategory = stationIds.length > 0;
        const isGlobalCategory = legacyStationId === '' && !isMappedCategory;
        return isGlobalCategory || legacyStationId === selected || stationIds.includes(selected);
      })
    : categories;

  const getCategoryType = (category) => {
    if (!category?.category_type || category.category_type === 'other') return 'other';
    return category.category_type;
  };

  const getCategoryTypeMeta = (categoryType) => {
    switch (categoryType) {
      case 'music':
        return {
          label: 'Music',
          icon: <MusicNoteIcon sx={{ fontSize: '0.85rem' }} />,
          chipSx: { bgcolor: '#4caf5020', color: '#4caf50', fontSize: '0.7rem' },
          titleColor: '#4caf50',
        };
      case 'station_id':
        return {
          label: 'Station / ID',
          icon: <RadioIcon sx={{ fontSize: '0.85rem' }} />,
          chipSx: { bgcolor: '#ff980020', color: '#ff9800', fontSize: '0.7rem' },
          titleColor: '#ff9800',
        };
      case 'music_bed':
        return {
          label: 'Music Bed',
          icon: <HeadphonesIcon sx={{ fontSize: '0.85rem' }} />,
          chipSx: { bgcolor: '#e91e6320', color: '#e91e63', fontSize: '0.7rem' },
          titleColor: '#e91e63',
        };
      default:
        return {
          label: 'Other',
          icon: <MoreHorizIcon sx={{ fontSize: '0.85rem' }} />,
          chipSx: { bgcolor: '#9e9e9e20', color: '#9e9e9e', fontSize: '0.7rem' },
          titleColor: '#9e9e9e',
        };
    }
  };

  const renderTypeChip = (categoryType) => {
    const meta = getCategoryTypeMeta(categoryType);
    return <Chip icon={meta.icon} label={meta.label} size="small" sx={meta.chipSx} />;
  };

  const categoryTypeOrder = ['music', 'station_id', 'music_bed', 'other'];
  const categoriesWithTypeFilter = categoryTypeFilter
    ? filteredCategories.filter((c) => getCategoryType(c) === categoryTypeFilter)
    : filteredCategories;

  const categoriesByType = categoriesWithTypeFilter.reduce((acc, category) => {
    const categoryType = getCategoryType(category);
    if (!acc[categoryType]) acc[categoryType] = [];
    acc[categoryType].push(category);
    return acc;
  }, {});

  const orderedCategoryGroups = [
    ...categoryTypeOrder.filter((type) => categoriesByType[type]),
    ...Object.keys(categoriesByType).filter((type) => !categoryTypeOrder.includes(type)),
  ].map((type) => ({
    type,
    categories: categoriesByType[type],
  }));

  const renderCategoryCard = (category) => {
    const isFixed = category.is_fixed || category.name === 'Voicetrack' || category.name === 'Commercial Spots' || category.name === 'Music Beds';
    const categoryType = getCategoryType(category);
    return (
      <Card
        sx={{
          bgcolor: 'var(--rs-paper)',
          border: '1px solid var(--rs-border)',
          '&:hover': {
            borderColor: category.color || '#22d3ee',
          },
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '4px',
                  bgcolor: category.color || '#1976D2',
                }}
              />
              <Typography variant="h6" sx={{ color: categoryType === 'music_bed' ? '#e91e63' : '#22d3ee' }}>
                {category.name}
              </Typography>
              {isFixed && (
                <Chip
                  label="Fixed"
                  size="small"
                  sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.65rem', height: '20px' }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(category)}
                sx={{ color: '#22d3ee' }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(category.id)}
                disabled={isFixed}
                sx={{ color: isFixed ? '#666' : '#ff6b6b' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {category.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {category.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {renderTypeChip(categoryType)}
            {hasSystemFeature('ai_insights') && category.ai_insights_enabled && (
              <Chip
                label="AI Insights"
                size="small"
                sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.7rem' }}
              />
            )}
            {!category.metadata_enabled && (
              <Chip
                label="No Metadata"
                size="small"
                sx={{ bgcolor: '#666', color: '#999', fontSize: '0.7rem' }}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderCategoryListItem = (category) => {
    const isFixed = category.is_fixed || category.name === 'Voicetrack' || category.name === 'Commercial Spots' || category.name === 'Music Beds';
    const categoryType = getCategoryType(category);
    return (
      <Card
        key={category.id}
        sx={{
          bgcolor: 'var(--rs-paper)',
          border: '1px solid var(--rs-border)',
          '&:hover': {
            borderColor: category.color || '#22d3ee',
          },
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '4px',
                  bgcolor: category.color || '#1976D2',
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="subtitle1"
                sx={{
                  color: categoryType === 'music_bed' ? '#e91e63' : '#22d3ee',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {category.name}
              </Typography>
              {isFixed && (
                <Chip
                  label="Fixed"
                  size="small"
                  sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.65rem', height: '20px' }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {renderTypeChip(categoryType)}
              {hasSystemFeature('ai_insights') && category.ai_insights_enabled && (
                <Chip
                  label="AI Insights"
                  size="small"
                  sx={{ bgcolor: '#22d3ee20', color: '#22d3ee', fontSize: '0.7rem' }}
                />
              )}
              {!category.metadata_enabled && (
                <Chip
                  label="No Metadata"
                  size="small"
                  sx={{ bgcolor: '#666', color: '#999', fontSize: '0.7rem' }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => handleOpenDialog(category)}
                sx={{ color: '#22d3ee' }}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(category.id)}
                disabled={isFixed}
                sx={{ color: isFixed ? '#666' : '#ff6b6b' }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          {category.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {category.description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderGridSection = (sectionCategories) => (
    <Grid container spacing={2}>
      {sectionCategories.map((category) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
          {renderCategoryCard(category)}
        </Grid>
      ))}
    </Grid>
  );

  const renderListSection = (sectionCategories) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {sectionCategories.map((category) => renderCategoryListItem(category))}
    </Box>
  );

  return (
    <Box>
      <PageHeader
        overline="Media"
        title="Categories"
        subtitle="Organize your assets into scheduling categories"
        icon={<LabelIcon />}
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ bgcolor: '#22d3ee', '&:hover': { bgcolor: '#00b8e6' } }}
          >
            New Category
          </Button>
        }
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {stations.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Station</InputLabel>
            <Select
              value={selectedStationFilter}
              onChange={(e) => setSelectedStationFilter(e.target.value)}
              label="Station"
            >
              <MenuItem value="">All Stations</MenuItem>
              {stations.map((station) => (
                <MenuItem key={station.id} value={station.id}>
                  {station.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', ml: 'auto' }}>
          {filteredCategories.length > 8 && (
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value={categoryTypeFilter}
                onChange={(e) => setCategoryTypeFilter(e.target.value)}
                label="Filter by Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="music">Music</MenuItem>
                <MenuItem value="station_id">Station / ID</MenuItem>
                <MenuItem value="music_bed">Music Bed</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          )}
          <FormControl sx={{ minWidth: 130 }}>
            <InputLabel>View</InputLabel>
            <Select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              label="View"
            >
              <MenuItem value="grid">Grid</MenuItem>
              <MenuItem value="list">List</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 170 }}>
            <InputLabel>Organize</InputLabel>
            <Select
              value={organizeMode}
              onChange={(e) => setOrganizeMode(e.target.value)}
              label="Organize"
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="by_type">By Category Type</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {categoriesWithTypeFilter.length === 0 ? (
        <Card sx={{ bgcolor: 'var(--rs-paper)', border: '1px solid var(--rs-border)' }}>
          <CardContent>
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              {categoryTypeFilter ? 'No categories match the selected type.' : 'No categories created. Create your first category to organize your assets.'}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {organizeMode === 'by_type'
            ? orderedCategoryGroups.map((group) => {
                const meta = getCategoryTypeMeta(group.type);
                return (
                  <Box key={group.type}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {renderTypeChip(group.type)}
                      <Typography variant="subtitle2" sx={{ color: meta.titleColor }}>
                        {group.categories.length} {group.categories.length === 1 ? 'category' : 'categories'}
                      </Typography>
                    </Box>
                    {viewMode === 'grid' ? renderGridSection(group.categories) : renderListSection(group.categories)}
                  </Box>
                );
              })
            : viewMode === 'grid'
              ? renderGridSection(categoriesWithTypeFilter)
              : renderListSection(categoriesWithTypeFilter)}
        </Box>
      )}

      {/* Category Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelIcon sx={{ color: '#22d3ee' }} />
            <Typography variant="h6">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && (
            <Box sx={{ mb: 2, p: 1, bgcolor: '#ff6b6b20', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ color: '#ff6b6b' }}>
                {error}
              </Typography>
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Category Name *"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={editingCategory && (editingCategory.name === 'Voicetrack' || editingCategory.name === 'Commercial Spots' || editingCategory.name === 'Music Beds')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {stations.length > 1 && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Stations</InputLabel>
                  <Select
                    multiple
                    value={formData.station_ids || []}
                    onChange={(e) => {
                      const value = e.target.value;
                      const selectedIds = (Array.isArray(value) ? value : String(value).split(','))
                        .map((id) => String(id))
                        .filter((id) => id !== '');
                      setFormData({
                        ...formData,
                        station_ids: selectedIds,
                        station_id: selectedIds.length === 1 ? Number(selectedIds[0]) : null,
                      });
                    }}
                    label="Stations"
                    renderValue={(selected) => {
                      const ids = Array.isArray(selected) ? selected.map((id) => String(id)) : [];
                      if (ids.length === 0) return 'All Stations';
                      return stations
                        .filter((station) => ids.includes(String(station.id)))
                        .map((station) => station.name)
                        .join(', ');
                    }}
                  >
                    {stations.map((station) => (
                      <MenuItem key={station.id} value={String(station.id)}>
                        {(formData.station_ids || []).includes(String(station.id)) ? '✓ ' : ''}{station.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControl component="fieldset" sx={{ mt: 1 }}>
                <FormLabel component="legend" sx={{ color: '#22d3ee', '&.Mui-focused': { color: '#22d3ee' } }}>
                  Category Type
                </FormLabel>
                <RadioGroup
                  value={formData.category_type || 'other'}
                  onChange={(e) => setFormData({ ...formData, category_type: e.target.value })}
                >
                  <FormControlLabel
                    value="music"
                    control={<Radio sx={{ color: '#4caf50', '&.Mui-checked': { color: '#4caf50' } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MusicNoteIcon sx={{ fontSize: '1rem', color: '#4caf50' }} />
                        Music
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="station_id"
                    control={<Radio sx={{ color: '#ff9800', '&.Mui-checked': { color: '#ff9800' } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <RadioIcon sx={{ fontSize: '1rem', color: '#ff9800' }} />
                        Station / ID
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="music_bed"
                    control={<Radio sx={{ color: '#e91e63', '&.Mui-checked': { color: '#e91e63' } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HeadphonesIcon sx={{ fontSize: '1rem', color: '#e91e63' }} />
                        Music Bed
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="other"
                    control={<Radio sx={{ color: '#9e9e9e', '&.Mui-checked': { color: '#9e9e9e' } }} />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <MoreHorizIcon sx={{ fontSize: '1rem', color: '#9e9e9e' }} />
                        Other
                      </Box>
                    }
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Music categories support autofade early-fade. Station/ID categories are protected from back-to-back scheduling before ad breaks. Music Bed categories hold audio for mixing under voicetracks and AI content.
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                disabled={!hasSystemFeature('ai_insights') && !formData.ai_insights_enabled}
                control={
                  <Switch
                    checked={!!formData.ai_insights_enabled}
                    onChange={(e) => {
                      if (e.target.checked && !hasSystemFeature('ai_insights')) return;
                      setFormData({ ...formData, ai_insights_enabled: e.target.checked });
                    }}
                    disabled={!hasSystemFeature('ai_insights') && !formData.ai_insights_enabled}
                  />
                }
                label="Enable AI Insights"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.metadata_enabled}
                    onChange={(e) => setFormData({ ...formData, metadata_enabled: e.target.checked })}
                  />
                }
                label="Enable Metadata Passing"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#22d3ee' }}>
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
